import axios, { type AxiosError } from "axios";

import { AptiError } from "@/core/errors";
import { captureErrorMessage } from "@/lib/helpers/captureErrorMessage";

/**
 * Global mutation error handler for React Query
 *
 * Handles four types of errors:
 * 1. AptiError instances (base class for all custom app errors)
 * 2. Custom error classes with wrapped Axios errors (legacy pattern)
 * 3. Direct Axios errors from API calls
 * 4. Generic JavaScript errors
 *
 * Logs all errors to LogRocket with rich contextual metadata for debugging.
 *
 * @param error - The error thrown by a mutation
 *
 * @example
 * ```ts
 * // Usage in QueryClient configuration
 * const queryClient = new QueryClient({
 *   defaultOptions: {
 *     mutations: {
 *       onError: handleMutationError,
 *     },
 *   },
 * });
 * ```
 */
export function handleMutationError(error: unknown): void {
  let axiosError: AxiosError | null = null;
  let errorMessage = "";
  let errorCode: string | undefined;
  let context: Record<string, unknown> | undefined;

  // Check if error is an AptiError instance
  if (error instanceof AptiError) {
    errorCode = error.code;
    context = error.context;
    errorMessage = `API Mutation Error [${error.constructor.name}]: ${error.message}`;

    // Extract Axios error from cause if present
    if (axios.isAxiosError(error.cause)) {
      axiosError = error.cause;
    }

    // Use statusCode from AptiError if no axios error
    if (!axiosError && error.statusCode) {
      context = { ...context, statusCode: error.statusCode };
    }
  } else if (error instanceof Error && "cause" in error && axios.isAxiosError(error.cause)) {
    // Legacy pattern: custom error class with wrapped Axios cause
    axiosError = error.cause;
    errorCode = "code" in error && typeof error.code === "string" ? error.code : undefined;
    errorMessage = `API Mutation Error [${error.constructor.name}]: ${error.message}`;
  } else if (axios.isAxiosError(error)) {
    // Direct Axios error
    axiosError = error;
    errorMessage = `API Mutation Error: ${error.response?.status} ${error.response?.statusText || error.message}`;
  } else {
    // Generic error fallback
    errorMessage = `Mutation Error: ${error instanceof Error ? error.message : "Unknown error"}`;
  }

  captureErrorMessage(errorMessage, {
    tags: {
      ...context,
      errorClass: error instanceof Error ? error.constructor.name : undefined,
      errorCode,
      errorType: "mutation",
      statusCode: axiosError?.response?.status,
      url: axiosError?.config?.url,
    },
  });
}
